import React, { useState } from 'react';
import Logo from './Logo';
import ForgotPassword from './ForgotPassword';
import OTPAuth from './OTPAuth';

interface LoginProps {
  onLogin: (email: string, password: string) => void;
  onRegister: (data: any) => void;
  onOTPLogin: (token: string, partner: any) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin, onRegister, onOTPLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [useOTP, setUseOTP] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [vehicleType, setVehicleType] = useState('Bike');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [loading, setLoading] = useState(false);

  // Show Forgot Password component
  if (showForgotPassword) {
    return (
      <ForgotPassword
        onBack={() => setShowForgotPassword(false)}
        onSuccess={() => {
          setShowForgotPassword(false);
          setIsLogin(true);
        }}
      />
    );
  }

  // Show OTP Auth component
  if (useOTP) {
    return (
      <div>
        <OTPAuth onSuccess={onOTPLogin} />
        <div style={{
          textAlign: 'center',
          marginTop: '20px'
        }}>
          <button
            onClick={() => setUseOTP(false)}
            style={{
              background: 'transparent',
              color: '#4CAF50',
              border: '2px solid #4CAF50',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            ← Use Email/Password Instead
          </button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await onLogin(email, password);
      } else {
        await onRegister({
          name,
          email,
          password,
          phone,
          vehicleType,
          vehicleNumber,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={{display: 'flex', justifyContent: 'center', marginBottom: '20px'}}>
          <Logo size={60} showText={true} />
        </div>
        <h2 style={{...styles.subtitle, marginTop: '10px'}}>Partner {isLogin ? 'Login' : 'Register'}</h2>

        <form onSubmit={handleSubmit} style={styles.form}>
          {!isLogin && (
            <>
              <input
                type="text"
                placeholder="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={styles.input}
                required
              />
              <input
                type="tel"
                placeholder="Phone Number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                style={styles.input}
                required
              />
              <select
                value={vehicleType}
                onChange={(e) => setVehicleType(e.target.value)}
                style={styles.input}
                required
              >
                <option value="Bike">Bike</option>
                <option value="Scooter">Scooter</option>
                <option value="Car">Car</option>
                <option value="Bicycle">Bicycle</option>
              </select>
              <input
                type="text"
                placeholder="Vehicle Number (e.g., TS09AB1234)"
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value)}
                style={styles.input}
                required
              />
            </>
          )}

          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
            required
          />

          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={styles.input}
            required
          />

          <button
            type="submit"
            style={styles.button}
            disabled={loading}
          >
            {loading ? 'Please wait...' : (isLogin ? 'Login' : 'Register')}
          </button>

          {/* Forgot Password Link */}
          {isLogin && (
            <div style={{ textAlign: 'center', marginTop: '10px' }}>
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#4CAF50',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                🔐 Forgot Password?
              </button>
            </div>
          )}
        </form>

        {isLogin && (
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <button
              type="button"
              onClick={() => setUseOTP(true)}
              style={{
                background: '#4CAF50',
                color: 'white',
                border: 'none',
                padding: '12px 30px',
                borderRadius: '25px',
                fontSize: '16px',
                cursor: 'pointer',
                width: '100%',
                fontWeight: 'bold'
              }}
            >
              📱 Login with OTP
            </button>
          </div>
        )}

        <p style={styles.toggle}>
          {isLogin ? "Don't have an account? " : 'Already have an account? '}
          <span
            onClick={() => setIsLogin(!isLogin)}
            style={styles.link}
          >
            {isLogin ? 'Register' : 'Login'}
          </span>
        </p>
      </div>
    </div>
  );
};

const styles: any = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #fc8019 0%, #ff6347 100%)',
    padding: '20px',
  },
  card: {
    background: 'white',
    borderRadius: '20px',
    padding: '40px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
    maxWidth: '450px',
    width: '100%',
  },
  title: {
    fontSize: '32px',
    color: '#fc8019',
    textAlign: 'center',
    marginBottom: '10px',
  },
  subtitle: {
    fontSize: '24px',
    color: '#333',
    textAlign: 'center',
    marginBottom: '30px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  input: {
    padding: '15px',
    fontSize: '16px',
    border: '2px solid #e0e0e0',
    borderRadius: '10px',
    outline: 'none',
    transition: 'border 0.3s',
  },
  button: {
    padding: '15px',
    fontSize: '18px',
    fontWeight: 'bold',
    color: 'white',
    background: '#fc8019',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    marginTop: '10px',
    transition: 'background 0.3s',
  },
  toggle: {
    textAlign: 'center',
    marginTop: '20px',
    color: '#666',
  },
  link: {
    color: '#fc8019',
    fontWeight: 'bold',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
};

export default Login;
